import React, { useMemo } from 'react';
import { PropTypes } from 'prop-types';
import { Icon, Spacer } from '@ontruck/orc';
import { isEmpty } from 'utils/types';
import cx from 'classnames';
import AdditionalOptions from 'components/orders/AdditionalOptions';
import MRAditionalOptions from 'components/summary/MRAditionalOptions';
import StandstillIncident from './components/StandstillIncident';
import ProgressBar from 'components/orders/ProgressBar';
import { ORDER_TYPES } from 'utils/order/constants';
import { useTranslation } from 'react-i18next';
import { useOrderEta } from 'hooks/useOrderEta';
import StatusText from 'components/orders/ShipmentCard/Status/StatusText';
import { CalendarIcon } from 'refactored/ui/icons';
import { formatTimeToRoundedHourRange } from 'utils/driverEta';
import moment from 'moment-timezone';
import { useOrderStop } from './UseOrderStop';
import { useOrder } from 'hooks/useOrders';
import LoadsSummaryTable from 'refactored/features/order/loads/displays/LoadsSummaryTable';
import { IS_TENANT } from 'tenant/utils';
import LoadsSummaryTableTenant from 'refactored/features/order/loads/displays/LoadsSummaryTableTenant';

const OrderStop = ({
  className,
  orderId,
  orderType,
  isRecurring,
  stopIndex,
  stops,
}) => {
  const { t } = useTranslation('OrderStop');
  const { eta } = useOrderEta(orderId, isRecurring);
  const {
    stop,
    stopType,
    loads,
    addressFullData,
    getFullDate,
    isFinished,
    stopName,
    appointmentDate,
  } = useOrderStop();

  const isCurrentStopWithDelay = useMemo(
    () => eta?.isDelayed && eta?.stopIndex === stopIndex,
    [eta, stopIndex]
  );

  const estimatedTime = useMemo(() => {
    return isCurrentStopWithDelay
      ? formatTimeToRoundedHourRange(moment(eta.eta).tz(stop.time_to_tz))
      : null;
  }, [eta, stop, isCurrentStopWithDelay]);

  const shipmentKeys = useMemo(() => {
    return Object.keys(stop?.shipments || {});
  }, [stop]);

  const shipmentId = useMemo(() => {
    return shipmentKeys[0] || '';
  }, [shipmentKeys]);

  const { order } = useOrder(orderId, isRecurring);

  const showInternalReference = useMemo(
    () =>
      ((ORDER_TYPES.DISTRIBUTION.includes(orderType) &&
        stopType === 'dropoff') ||
        (ORDER_TYPES.COLLECTION.includes(orderType) && stopType === 'pickup') ||
        (ORDER_TYPES.SINGLE_SHIPMENT.includes(orderType) &&
          stopType === 'dropoff')) &&
      (!IS_TENANT ||
        (IS_TENANT && stopIndex > 0 && stopIndex < [order.stops.length - 1])),
    [orderType, stopType, stopIndex, order]
  );
  const { internalReference, comments, appointmentInstructions } =
    useMemo(() => {
      const firstShipment = order?.shipments?.[0];
      const stopComment = `${stop.stop_type.toLowerCase()}_comment_from_shipper_to_driver`;
      return {
        internalReference: firstShipment?.internal_reference,
        comments: firstShipment?.[stopComment],
        appointment_instructions: firstShipment?.appointment_instructions,
      };
    }, [order, stop]);

  return (
    <div
      className={cx(className, 'order-stop', 'warning')}
      data-testid="order-stop"
    >
      <ProgressBar />
      <div className="step-data-wrapper">
        {isCurrentStopWithDelay ? (
          <StatusText
            subTitle={{
              value: 'delayed',
              estimatedTime,
              style: 'warning',
            }}
          />
        ) : (
          <div className="date">
            {!appointmentDate && <span>{getFullDate()}</span>}
            {appointmentDate && (
              <>
                <CalendarIcon />
                {` ${appointmentDate}`}
              </>
            )}
          </div>
        )}
        <StandstillIncident stop={stop} />
        <h3 className="name">{stopName}</h3>
        <div className="address">{addressFullData}</div>
        <Spacer className={`load-info-wrapper  `} mTop="M">
          {!IS_TENANT && <LoadsSummaryTable loads={loads} />}
          {IS_TENANT && (
            <>
              <LoadsSummaryTableTenant
                stops={stops}
                loads={loads}
                stopIndex={stopIndex}
              />
            </>
          )}

          {(stop.cargo_manifests || [])
            .filter((cargoManifest) => !!cargoManifest.cargo_manifest_url)
            .map((cargoManifest) => (
              <a
                key={cargoManifest && cargoManifest.cargo_manifest_url}
                href={cargoManifest && cargoManifest.cargo_manifest_url}
                rel="noopener noreferrer"
                target="_blank"
              >
                <div
                  className="download-button"
                  role="presentation"
                  data-testid="cargo-manifest"
                >
                  <Icon
                    className="download-icon"
                    glyph="down"
                    size="12px"
                    color="grey700"
                  />
                  <span className="label">{t('download_label')}</span>
                </div>
              </a>
            ))}
        </Spacer>

        <AdditionalOptions
          orderId={orderId}
          shipmentId={shipmentId}
          comments={stop.comments || comments}
          internalReference={stop.internal_reference || internalReference}
          showInternalReference={showInternalReference}
          appointmentInstructions={appointmentInstructions}
          isRecurring={isRecurring}
          hideAdditionalDocuments
        />
        {stop.shipmentKey && (
          <MRAditionalOptions
            shipmentKey={stop.shipmentKey}
            stopType={stopType}
            t={t}
            orderType={orderType}
          />
        )}
        {(!isEmpty(stop.driver) || !isEmpty(stop.vehicles)) && isFinished && (
          <Spacer mTop="L" mBottom="L">
            <div className="assigned__wrapper">
              {!isEmpty(stop.driver) && (
                <div className="assigned__driver" data-testid="assigned-driver">
                  <span className="assigned__driver-icon">
                    <Icon glyph="status-driver" size="1.8rem" />
                  </span>
                  <span className="assigned__driver-label">{`${t(
                    'OrderStop:label.driver.transporter'
                  )}: `}</span>
                  <span className="assigned__driver-name">
                    {stop.driver.name}
                  </span>
                </div>
              )}
              {!isEmpty(stop.vehicles) && (
                <Spacer mTop="XS" mBottom="XS">
                  <div
                    className="assigned__vehicle"
                    data-testid="assigned-vehicle"
                  >
                    {stop.vehicles
                      .map((vehicle) => `${vehicle.plate_number}`)
                      .join(' + ')}
                  </div>
                </Spacer>
              )}
            </div>
          </Spacer>
        )}
      </div>
    </div>
  );
};

OrderStop.propTypes = {
  className: PropTypes.string,
  orderId: PropTypes.string.isRequired,
  orderType: PropTypes.string.isRequired,
  stopIndex: PropTypes.number.isRequired,
  isMultiRoute: PropTypes.bool,
  isRecurring: PropTypes.bool,
};

OrderStop.defaultProps = {
  isMultiRoute: false,
  isRecurring: false,
};

export default OrderStop;
